/**
 * useCropsArea — TanStack Query hook для сводной площади по культурам.
 *
 * Endpoint: GET /api/v1/aggregations/crops-area?scope_type=...&scope_id=...
 *
 * Возвращает массив {crop_id, name, color, total_ha, fields_count}, отсортированный
 * по total_ha DESC. crop_id='no_crop' — это поля без посева в scope (показываются
 * grey-row "Без посева" в CropFilterPanel v2 / AK-70).
 *
 * Scope-aware: при смене activeScope (переключатель хозяйства в сайдбаре) → новая queryKey →
 * автоматический refetch. Дополнительный invalidate из CropFilterPanel на событие
 * scopeChanged (через useScopeChanged) — чтобы свежие посевы (созданные в Field
 * detail) тоже подтянулись.
 *
 * staleTime 60s: посевы меняются редко, но не вечны. Для UX «открыл /map → вижу
 * актуальные площади» 60s достаточно, на каждом ре-рендере панели мы не фетчим.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ActiveScope } from '@/lib/map/useMapStore';

export interface CropAreaItem {
  /** UUID культуры или 'no_crop' для полей без посева. */
  crop_id: string;
  /** Локализованное имя культуры или 'Без посева'. */
  name: string;
  /** Hex-цвет swatch'а, default #9CA3AF (grey). */
  color: string;
  /** Сумма area_ha по полям с этой культурой в scope. */
  total_ha: number;
  /** Количество полей с этой культурой в scope. */
  fields_count: number;
}

export interface CropsAreaResponse {
  items: CropAreaItem[];
  total_ha: number;
  total_fields: number;
}

export function useCropsArea(scope: ActiveScope): UseQueryResult<CropsAreaResponse, Error> {
  const isCompany = scope.type === 'company' || !scope.id;
  const params = new URLSearchParams({
    scope_type: scope.type,
  });
  if (!isCompany && scope.id) {
    params.set('scope_id', scope.id);
  }

  return useQuery<CropsAreaResponse, Error>({
    queryKey: ['crops-area', scope.type, scope.id ?? 'company'],
    queryFn: async () => {
      const resp = await api.get<CropsAreaResponse>(
        `/api/v1/aggregations/crops-area?${params.toString()}`,
      );
      return resp;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}
